import { SUPPORT_PAGE_URL } from '../api';
import { Motif, Wordmark, currentTheme } from '../brand/Brand';

// Error pages (D-82): plain words, the unlit motif, the address in mono on a 404,
// and one clear next step. Replaces the 1.0 CRT / dmesg page.

interface ErrorCopy {
  title: string;
  body: string;
  primary: { label: string; href: string };
  secondary: { label: string; href: string };
  seed: number;
}

const COPY: Record<string, ErrorCopy> = {
  '404': {
    title: 'This page doesn’t exist',
    body: 'The address may be mistyped, or the page has moved.',
    primary: { label: 'Go to your ideas', href: '/app' },
    secondary: { label: 'Home', href: '/' },
    seed: 58,
  },
  '500': {
    title: 'Something broke on our end',
    body: 'Your ideas are safe. Try again in a moment.',
    primary: { label: 'Try again', href: '' },
    secondary: { label: 'Go to your ideas', href: '/app' },
    seed: 12,
  },
  '400': {
    title: 'This sign-in link has expired',
    body: 'Sign-in links work once, within 15 minutes. Ask for a new one.',
    primary: { label: 'Send a new link', href: '/login' },
    secondary: { label: 'Home', href: '/' },
    seed: 77,
  },
  '401': {
    title: 'Sign in to see this page',
    body: 'Your session ended, or you signed out on another device.',
    primary: { label: 'Sign in', href: '/login' },
    secondary: { label: 'Home', href: '/' },
    seed: 40,
  },
  '403': {
    title: 'You don’t have access to this page',
    body: 'It belongs to another account, or your plan doesn’t include it.',
    primary: { label: 'Go to your ideas', href: '/app' },
    secondary: { label: 'Home', href: '/' },
    seed: 21,
  },
};

export function ErrorPage() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code') || '404';
  const copy = COPY[code] ?? COPY['500']!;
  // A server-supplied message (e.g. from /auth) is shown under the title, as text only.
  const detail = params.get('message');
  const path = code === '404' && window.location.pathname !== '/error' ? window.location.host + window.location.pathname : null;
  const theme = currentTheme();

  return (
    <div className="lost">
      <header className="lost-head">
        <Wordmark size={18} href="/" />
      </header>
      <main className="lost-main">
        <Motif className="lost-motif" width={480} height={150} theme={theme} seed={copy.seed} pitch={18} fx={0.5} fy={0.5} mask="band" lit={false} dim={0.8} id="lost" />
        <span className="lost-code">{code}</span>
        <h1 className="lost-title">{copy.title}</h1>
        <p className="lost-body">{copy.body}</p>
        {detail && detail !== copy.title && <p className="lost-detail">{detail}</p>}
        {path && <code className="lost-path">{path}</code>}
        <div className="lost-actions">
          {copy.primary.href ? (
            <a className="fr-btn fr-btn-primary" href={copy.primary.href}>{copy.primary.label}</a>
          ) : (
            <button className="fr-btn fr-btn-primary" onClick={() => window.location.reload()}>{copy.primary.label}</button>
          )}
          <a className="fr-btn" href={copy.secondary.href}>{copy.secondary.label}</a>
        </div>
        {code === '500' && (
          <p className="lost-note">Still broken? <a href={SUPPORT_PAGE_URL}>Contact support</a>.</p>
        )}
      </main>
    </div>
  );
}
